import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography, Stack,
  CircularProgress, Alert, InputAdornment, IconButton,
  Dialog, DialogContent, DialogActions, Tooltip, Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Visibility, VisibilityOff, LockOutlined,
  CheckCircle as CheckIcon,
  School as SchoolIcon,
  Badge as BadgeIcon, Verified as VerifiedIcon,
  AdminPanelSettings as AdminIcon,
  FamilyRestroom as ParentIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import API from '../../BaseURL';

// Idle-state animations for the login screen. Keep them slow and small
// so they read as "system is alive" rather than "look at me". One short
// <style> block injected once, then individual elements opt-in via the
// `animation:` CSS shorthand on their sx prop.
const LOGIN_KEYFRAMES = `
  @keyframes login-blink   { 0%,100% { opacity:1; transform:scale(1); }
                              50% { opacity:0.35; transform:scale(0.85); } }
  @keyframes login-breathe { 0%,100% { transform:scale(1);   box-shadow:0 12px 40px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.2); }
                              50% { transform:scale(1.035); box-shadow:0 16px 48px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.28); } }
  @keyframes login-drift-a { 0%,100% { transform:translate(0,0)   scale(1); }
                              50% { transform:translate(-26px,18px) scale(1.06); } }
  @keyframes login-drift-b { 0%,100% { transform:translate(0,0)   scale(1); }
                              50% { transform:translate(22px,-14px) scale(1.08); } }
  @keyframes login-drift-c { 0%,100% { transform:translate(0,0); }
                              50% { transform:translate(-18px,-22px); } }
  @keyframes login-shine   { 0%   { background-position:-200% 0; }
                              100% { background-position: 200% 0; } }
  @keyframes login-ring    { 0% { transform:scale(1);   opacity:0.55; }
                              80% { transform:scale(2.4); opacity:0; }
                              100% { transform:scale(2.4); opacity:0; } }
  @keyframes login-glow    { 0%,100% { box-shadow:0 0 0 0 rgba(34,197,94,0.55); }
                              50% { box-shadow:0 0 0 6px rgba(34,197,94,0); } }

  /* ── Entrance ──────────────────────────────────────────────────
     The page assembles itself once, top to bottom, instead of
     appearing all at once. Small distances and a short duration: this
     should register as polish, not as a screen the user has to wait
     for. The fill mode is "both", which holds the opening frame so
     nothing flashes at full opacity before its turn.
     (No backticks in here — this block is itself a template literal.) */
  @keyframes login-rise    { from { opacity:0; transform:translateY(14px); }
                              to  { opacity:1; transform:none; } }
  @keyframes login-fade    { from { opacity:0; } to { opacity:1; } }

  /* A signed-in school is a serious thing; the brand mark gets one
     unhurried sweep rather than a bounce. */
  @keyframes login-sweep   { from { opacity:0; transform:translateX(-18px) scale(0.96); }
                              to  { opacity:1; transform:none; } }

  /* Every animation on this screen is decorative. Anyone who has asked
     their system to stop moving things gets a still page — the login
     form works identically either way. */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.001ms !important;
    }
  }
`;

// Staggered entrance. Returns the `animation` shorthand for the nth
// element in a sequence so the order is declared where the element is,
// not tracked in a separate list that drifts out of step with the JSX.
const rise = (delayMs = 0, name = 'login-rise') =>
  `${name} 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms both`;

// Pick where a user lands after login. Collectors-only (their only assigned
// page is the collector sheet) go straight into the kiosk-style /collect
// screen so they never see the dashboard or sidebar menus.
function landingPath(user) {
  if (!user) return '/login';
  if (user.role === 'parent') return '/portal';
  if (user.role === 'super_admin') return '/dashboard';
  const pages = user.pages || [];
  if (pages.length === 1 && pages[0] === 'collector_sheet') return '/collect';
  return '/dashboard';
}

function getStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '' };
  let s = 0;
  if (pw.length >= 8)           s++;
  if (/[A-Z]/.test(pw))         s++;
  if (/[0-9]/.test(pw))         s++;
  if (/[^A-Za-z0-9]/.test(pw))  s++;
  return [
    { score: 1, label: 'Weak',   color: '#ef5350' },
    { score: 2, label: 'Fair',   color: '#FFA726' },
    { score: 3, label: 'Good',   color: '#29B6F6' },
    { score: 4, label: 'Strong', color: '#66BB6A' },
  ][s - 1] || { score: 0, label: '', color: '' };
}

function SchoolCrest({ size = 80 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <path d="M50 8 L88 22 L88 55 Q88 80 50 95 Q12 80 12 55 L12 22 Z"
        fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.5" />
      <path d="M30 42 L50 32 L70 42 L50 52 Z"
        fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2" />
      <line x1="70" y1="42" x2="70" y2="56" stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round"/>
      <path d="M36 48 L36 60 Q50 70 64 60 L64 48"
        fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2" />
      <rect x="38" y="68" width="24" height="14" rx="1.5"
        fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.6" />
      <line x1="50" y1="68" x2="50" y2="82" stroke="rgba(255,255,255,0.55)" strokeWidth="1" />
      <line x1="38" y1="75" x2="62" y2="75" stroke="rgba(255,255,255,0.55)" strokeWidth="1" />
    </svg>
  );
}

// ── The parent's first sign-in ──────────────────────────────────
//
// A separate dialog from the staff one, and it has to be. A parent
// arrives here from an SMS, on a phone, having typed six digits from a
// text message — and the staff dialog would meet them with "at least
// one uppercase letter". The credential shapes are different, so the
// screens are different.
//
// Six boxes rather than one field: it mirrors the code they just typed,
// it makes a mistyped digit visible instead of hidden behind dots, and
// it is what every bank app in the country has already taught them.
function PasscodeBoxes({ value, onChange, disabled, autoFocus, label, id, focusSignal }) {
  const refs = useRef([]);
  const digits = String(value || '').padEnd(6, ' ').slice(0, 6).split('');

  // Put the caret back in the first box when the caller asks — bump
  // `focusSignal` to a new number. Used after a rejected passcode: the
  // boxes have just been emptied, and without this the caret is left
  // sitting in the last box of a cleared row, so the first digit the
  // parent types lands in position six.
  //
  // `disabled` is a dependency, not just a guard. On React 17 the state
  // updates inside an async catch are not batched, so the clear renders
  // BEFORE loading flips back to false — focus() on a still-disabled
  // input does nothing at all. Depending on it means the effect runs
  // again the moment the boxes are live, whichever order they land in.
  useEffect(() => {
    if (!focusSignal || disabled) return;
    refs.current[0]?.focus();
  }, [focusSignal, disabled]);

  const setAt = (i, ch) => {
    const next = String(value || '').padEnd(6, ' ').split('');
    next[i] = ch;
    onChange(next.join('').replace(/\s+$/, '').trim());
  };

  const onKey = (i) => (e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[i] !== ' ' && digits[i] !== undefined) setAt(i, ' ');
      else if (i > 0) { setAt(i - 1, ' '); refs.current[i - 1]?.focus(); }
      return;
    }
    if (e.key === 'ArrowLeft'  && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < 5) refs.current[i + 1]?.focus();
  };

  const onInput = (i) => (e) => {
    const only = String(e.target.value || '').replace(/\D/g, '');
    if (!only) return;
    // Paste of a whole code lands in one box — spread it across all six
    // rather than making the parent type it again.
    if (only.length > 1) {
      onChange(only.slice(0, 6));
      refs.current[Math.min(only.length, 5)]?.focus();
      return;
    }
    setAt(i, only[0]);
    if (i < 5) refs.current[i + 1]?.focus();
  };

  return (
    <Box>
      <Typography component="label" htmlFor={`${id}-0`} sx={{
        fontSize: 12.5, fontWeight: 700, color: 'text.secondary',
        display: 'block', mb: 0.8,
      }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1}>
        {[0, 1, 2, 3, 4, 5].map(i => (
          <TextField
            key={i}
            id={`${id}-${i}`}
            inputRef={el => { refs.current[i] = el; }}
            value={digits[i] === ' ' ? '' : digits[i]}
            onChange={onInput(i)}
            onKeyDown={onKey(i)}
            onFocus={e => e.target.select()}
            disabled={disabled}
            autoFocus={autoFocus && i === 0}
            inputProps={{
              inputMode: 'numeric', autoComplete: 'one-time-code',
              maxLength: 6, 'aria-label': `${label} digit ${i + 1}`,
              style: { textAlign: 'center', fontSize: 22, fontWeight: 800, padding: '10px 0' },
            }}
            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        ))}
      </Stack>
    </Box>
  );
}

function SetPasscodeDialog({ open, user, onSuccess }) {
  const theme = useTheme();
  const p = theme.palette.primary.main;
  const [pc, setPc]           = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [done, setDone]       = useState(false);
  // Bumped to re-focus the first box after the boxes are cleared.
  const [focusSignal, setFocusSignal] = useState(0);

  const sixDigits = /^\d{6}$/.test(pc);
  const weak = /^(\d)\1{5}$/.test(pc)
    || '01234567890'.includes(pc) || '09876543210'.includes(pc);
  const reqs = [
    { label: 'Exactly 6 digits', met: sixDigits },
    { label: 'Not all the same digit, and not in sequence', met: sixDigits && !weak },
    { label: 'Both entries match', met: sixDigits && pc === confirm },
  ];
  const allMet = reqs.every(r => r.met);

  const submit = async () => {
    if (!allMet) return;
    setLoading(true); setError('');
    try {
      await API.post('/auth/parent/passcode', { new_passcode: pc });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save your passcode. Please try again.');
      // The passcode itself was refused — most often "choose one
      // different from the code we texted you". Wipe both rows and send
      // the caret back to the first box, because the alternative is
      // twelve backspaces on a phone before the parent can even start
      // over, and the boxes give no hint that clearing them is on them.
      //
      // Deliberately ONLY on 400, the status the server uses when it has
      // judged the digits. A dropped connection or a 500 leaves them
      // alone: those digits were fine, the request simply never landed,
      // and making a parent retype twelve of them because the network
      // blinked would be a worse bug than the one this fixes. They just
      // press Save again.
      if (err.response?.status === 400) {
        setPc('');
        setConfirm('');
        setFocusSignal(n => n + 1);
      }
    } finally { setLoading(false); }
  };

  const child = user?.parent?.student_name || 'your child';

  return (
    <Dialog open={open} maxWidth="xs" fullWidth disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${p} 0%, ${p}cc 100%)`,
        px: 3, pt: 3.5, pb: 2.5, color: '#fff', textAlign: 'center',
      }}>
        <Box sx={{
          width: 56, height: 56, borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mx: 'auto', mb: 1.5,
        }}>
          <LockOutlined sx={{ fontSize: 28, color: '#fff' }} />
        </Box>
        <Typography variant="h6" fontWeight={800}>
          {done ? 'Passcode saved' : 'Create your passcode'}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          {done
            ? 'Sign in with it to open the portal'
            : `The code we texted you was one-time only. Choose 6 digits you will remember — for ${child}.`}
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {done ? (
          <Stack spacing={2} alignItems="center">
            <CheckIcon sx={{ fontSize: 46, color: 'success.main' }} />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Your passcode is set. Nobody at the school can see it — if you forget it,
              ask the office to text you a new one-time code.
            </Typography>
            <Button fullWidth variant="contained" size="large" onClick={onSuccess}
              sx={{ fontWeight: 800, borderRadius: 2 }}>
              Go to sign in
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            <PasscodeBoxes id="pc" label="New 6-digit passcode" value={pc}
              onChange={setPc} disabled={loading} autoFocus
              focusSignal={focusSignal} />
            <PasscodeBoxes id="pc2" label="Type it again" value={confirm}
              onChange={setConfirm} disabled={loading} />
            <Stack spacing={0.6}>
              {reqs.map((r, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <CheckIcon sx={{
                    fontSize: 15,
                    color: r.met ? 'success.main' : 'action.disabled',
                  }} />
                  <Typography sx={{
                    fontSize: 12,
                    color: r.met ? 'text.primary' : 'text.secondary',
                  }}>
                    {r.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Button fullWidth variant="contained" size="large"
              disabled={!allMet || loading} onClick={submit}
              sx={{ fontWeight: 800, borderRadius: 2, py: 1.2 }}>
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Save my passcode'}
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChangePasswordDialog({ open, onSuccess }) {
  const theme = useTheme();
  const p = theme.palette.primary.main;
  const [form, setForm]       = useState({ new_password: '', confirm_password: '' });
  const [show, setShow]       = useState({ new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const pw       = form.new_password;
  const strength = getStrength(pw);
  const isMatch    = pw && form.confirm_password && pw === form.confirm_password;
  const isMismatch = form.confirm_password && pw !== form.confirm_password;

  const reqs = [
    { label: 'At least 6 characters', met: pw.length >= 6 },
    { label: 'At least one uppercase letter', met: /[A-Z]/.test(pw) },
    { label: 'At least one number', met: /[0-9]/.test(pw) },
    { label: 'Passwords match', met: !!isMatch },
  ];
  const allMet = reqs.every(r => r.met);

  const handleSubmit = async () => {
    if (!pw || !form.confirm_password) { setError('Both fields are required'); return; }
    if (pw.length < 6)                 { setError('Password must be at least 6 characters'); return; }
    if (pw !== form.confirm_password)  { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    try {
      await API.post('/auth/change-password', { current_password: '__skip__', new_password: pw });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update password.');
    } finally { setLoading(false); }
  };

  const strengthSegs = [1, 2, 3, 4];

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>

      <Box sx={{
        background: `linear-gradient(135deg, ${p} 0%, ${p}cc 100%)`,
        px: 4, pt: 4, pb: 3, color: '#fff', textAlign: 'center',
      }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mx: 'auto', mb: 2,
        }}>
          <LockOutlined sx={{ fontSize: 32, color: '#fff' }} />
        </Box>
        <Typography variant="h5" fontWeight={800} letterSpacing={-0.5}>
          Change Your Password
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
          Create a strong password to secure your account
        </Typography>
        <Chip label="First Login Required" size="small"
          sx={{ mt: 1.5, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: 11 }} />
      </Box>

      <DialogContent sx={{ px: 4, pt: 3, pb: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase"
          letterSpacing={0.8} display="block" mb={0.75}>
          New Password
        </Typography>
        <TextField fullWidth size="medium"
          type={show.new ? 'text' : 'password'} value={pw}
          placeholder="Enter a strong password"
          onChange={e => setForm({ ...form, new_password: e.target.value })}
          sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          InputProps={{ endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" edge="end" onClick={() => setShow(s => ({ ...s, new: !s.new }))}>
                {show.new ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )}}
        />

        {pw && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75 }}>
              {strengthSegs.map(i => (
                <Box key={i} sx={{
                  flex: 1, height: 5, borderRadius: 3,
                  bgcolor: i <= strength.score ? strength.color : '#e0e0e0',
                  transition: 'background-color 0.3s',
                }} />
              ))}
            </Box>
            <Typography variant="caption" fontWeight={700} sx={{ color: strength.color }}>
              {strength.label} password
            </Typography>
          </Box>
        )}

        <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase"
          letterSpacing={0.8} display="block" mb={0.75}>
          Confirm Password
        </Typography>
        <TextField fullWidth size="medium"
          type={show.confirm ? 'text' : 'password'} value={form.confirm_password}
          placeholder="Re-enter your password"
          onChange={e => setForm({ ...form, confirm_password: e.target.value })}
          error={isMismatch}
          sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          InputProps={{ endAdornment: (
            <InputAdornment position="end">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isMatch && <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />}
                <IconButton size="small" edge="end" onClick={() => setShow(s => ({ ...s, confirm: !s.confirm }))}>
                  {show.confirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </Box>
            </InputAdornment>
          )}}
        />

        <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, mb: 1 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary"
            textTransform="uppercase" letterSpacing={0.8} display="block" mb={1}>
            Requirements
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
            {reqs.map(r => (
              <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{
                  width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: r.met ? '#16a34a' : '#e5e7eb',
                  transition: 'background-color 0.2s',
                }}>
                  {r.met && <CheckIcon sx={{ fontSize: 11, color: '#fff' }} />}
                </Box>
                <Typography variant="caption" color={r.met ? 'text.primary' : 'text.disabled'}
                  fontWeight={r.met ? 600 : 400}>
                  {r.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 4, pt: 2 }}>
        <Button fullWidth variant="contained" size="large" onClick={handleSubmit}
          disabled={loading || !allMet}
          sx={{
            py: 1.5, borderRadius: 2, fontWeight: 800, fontSize: '1rem',
            background: allMet ? `linear-gradient(135deg, ${p}, ${p}cc)` : undefined,
            boxShadow: allMet ? `0 4px 16px ${p}50` : undefined,
          }}>
          {loading
            ? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />Saving…</>
            : 'Set Password & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function LoginPage() {
  const theme = useTheme();
  // A parent arriving from the portal SMS lands on /login?ind=JESS_563.
  // Their child's index number is already the username, so filling it
  // saves them transcribing it from the text message — the code is the
  // only thing they should have to type.
  const [form, setForm] = useState(() => {
    try {
      const ind = new URLSearchParams(window.location.search).get('ind');
      return { username: ind ? String(ind).trim() : '', password: '' };
    } catch { return { username: '', password: '' }; }
  });
  const [showPass, setShowPass]       = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [forceChange, setForceChange] = useState(false);
  // Set when a parent signed in with a one-time code and owes us a
  // passcode. Holds the user so the dialog can greet them by child.
  const [parentSetup, setParentSetup] = useState(null);

  const { login } = useAuth();
  const navigate  = useNavigate();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    if (!form.username && !form.password) { setError('Please enter your username and password'); return; }
    if (!form.username) { setError('Username or email is required'); return; }
    if (!form.password) { setError('Password is required'); return; }
    setError(''); setLoading(true);
    try {
      const res = await API.post('/auth/login', form);
      const u = res.data.user;
      if (res.data.must_change_password) {
        // The token is stashed but NOT handed to AuthContext yet: a
        // setup-only token must not become a session, or the portal
        // becomes reachable with a code the school can read.
        localStorage.setItem('school_token', res.data.token);
        localStorage.setItem('school_user', JSON.stringify(u));
        if (u?.role === 'parent') setParentSetup(u);
        else setForceChange(true);
      } else {
        login(res.data.token, u);
        navigate(landingPath(u));
      }
    } catch (err) {
      if (!err.response) {
        setError('Cannot connect to server. Please check your internet connection and try again.');
      } else {
        setError(err.response.data?.message || 'Invalid username or password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // The parent signs in again with the passcode they just chose.
  //
  // Not logged straight in: the setup token is spent, and making them
  // type the passcode once proves it is the one they meant — a typo
  // they never test is a parent locked out of their own account.
  const handlePasscodeSet = () => {
    localStorage.removeItem('school_token');
    localStorage.removeItem('school_user');
    const ind = parentSetup?.parent?.student_ind || '';
    setParentSetup(null);
    setForm({ username: ind, password: '' });
    setError('');
  };

  const handlePasswordChanged = () => {
    const storedUser  = JSON.parse(localStorage.getItem('school_user'));
    const storedToken = localStorage.getItem('school_token');
    login(storedToken, storedUser);
    setForceChange(false);
    navigate(landingPath(storedUser));
  };

  const p  = theme.palette.primary.main;
  const p2 = theme.palette.secondary.main;
  const year = new Date().getFullYear();

  const gridBg = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='rgba(255,255,255,0.07)' stroke-width='1'%3E%3Crect x='0' y='0' width='60' height='60'/%3E%3C/g%3E%3C/svg%3E")`;

  return (
    <>
      <style>{LOGIN_KEYFRAMES}</style>
      <ChangePasswordDialog open={forceChange} onSuccess={handlePasswordChanged} />
      <SetPasscodeDialog
        open={!!parentSetup}
        user={parentSetup}
        onSuccess={handlePasscodeSet}
      />

      {/* 100dvh where it exists, 100vh everywhere else. On a phone,
          100vh counts the address bar that is not actually on screen,
          so the page comes out taller than the viewport and the card
          sits under a scroll it did not need.
          Written as an @supports block, NOT as ['100vh','100dvh'] — an
          array in `sx` is a list of BREAKPOINT values, so that spelling
          would hand phones the 100vh it was meant to spare them. */}
      <Box sx={{
        minHeight: '100vh',
        '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
        display: 'flex', flexDirection: { xs: 'column', lg: 'row' },
      }}>

        {/* LEFT PANEL — the brand side.
            On a laptop this is the full hero. On a phone it collapses to
            a slim banner: parents and teachers arrive here to sign in,
            and a screen of marketing between them and the password field
            is a screen they have to scroll past every single morning.
            The padding, not a second component, does the collapsing. */}
        <Box sx={{
          flex: { xs: 'none', lg: '1 1 55%' },
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          p: { xs: 2.5, sm: 4, md: 6, lg: 8 },
          background: `linear-gradient(150deg, ${p2} 0%, ${p} 60%)`,
          backgroundImage: `${gridBg}, linear-gradient(150deg, ${p2} 0%, ${p} 60%)`,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position:'absolute', width:500, height:500, borderRadius:'50%',
            background:'rgba(255,255,255,0.03)', top:-160, right:-160, pointerEvents:'none',
            animation:'login-drift-a 14s ease-in-out infinite' }} />
          <Box sx={{ position:'absolute', width:300, height:300, borderRadius:'50%',
            background:'rgba(255,255,255,0.04)', bottom:-80, left:-100, pointerEvents:'none',
            animation:'login-drift-b 18s ease-in-out infinite' }} />
          <Box sx={{ position:'absolute', width:180, height:180, borderRadius:'50%',
            border:'1px solid rgba(255,255,255,0.06)', bottom:120, right:40, pointerEvents:'none',
            animation:'login-drift-c 11s ease-in-out infinite' }} />

          {/* Live system-online indicator — small breathing dot top-right.
              Hidden on a phone: it would sit on top of the compact
              banner's own text at that width. */}
          <Box sx={{
            display:{ xs:'none', sm:'flex' },
            position:'absolute', top:18, right:22, zIndex:2,
            alignItems:'center', gap:1,
            px:1.4, py:0.6, borderRadius:20,
            bgcolor:'rgba(255,255,255,0.10)',
            border:'1px solid rgba(255,255,255,0.18)',
            backdropFilter:'blur(6px)',
          }}>
            <Box sx={{ position:'relative', width:10, height:10 }}>
              <Box sx={{
                position:'absolute', inset:0, borderRadius:'50%',
                bgcolor:'#22c55e',
                animation:'login-glow 1.6s ease-out infinite',
              }} />
              <Box sx={{
                position:'absolute', inset:0, borderRadius:'50%',
                bgcolor:'#22c55e',
                animation:'login-blink 1.6s ease-in-out infinite',
              }} />
            </Box>
            <Typography sx={{
              color:'rgba(255,255,255,0.92)', fontWeight:700,
              fontSize:10.5, letterSpacing:1.2, textTransform:'uppercase',
            }}>
              System Online
            </Typography>
          </Box>

          <Box sx={{ position:'relative', zIndex:1, width:'100%', maxWidth:500 }}>

            {/* Brand. Horizontal on a phone so the banner stays a
                banner; the crest shrinks rather than the name wrapping. */}
            <Box sx={{
              display:'flex', alignItems:'center',
              gap:{ xs:1.75, md:3 },
              mb:{ xs:0, lg:3.5 },
              animation: rise(0, 'login-sweep'),
            }}>
              <Box sx={{
                width:{ xs:52, sm:68, md:88 }, height:{ xs:52, sm:68, md:88 },
                borderRadius:{ xs:2.5, md:4 }, flexShrink:0,
                background:'rgba(255,255,255,0.12)',
                border:'1.5px solid rgba(255,255,255,0.28)',
                display:'flex', alignItems:'center', justifyContent:'center',
                backdropFilter:'blur(10px)',
                boxShadow:'0 12px 40px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.2)',
                animation:'login-breathe 4.2s ease-in-out infinite',
                transformOrigin:'center',
              }}>
                <SchoolCrest size={64} />
              </Box>
              <Box sx={{ minWidth:0 }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:0.5 }}>
                  <Box sx={{ height:1.5, width:{ xs:12, md:20 }, bgcolor:'rgba(255,255,255,0.4)', borderRadius:1 }} />
                  <Typography sx={{
                    color:'rgba(255,255,255,0.65)', fontWeight:700,
                    letterSpacing:{ xs:1.6, md:2.5 }, textTransform:'uppercase',
                    fontSize:{ xs:8.5, md:10 }, whiteSpace:'nowrap',
                  }}>
                    Education Portal
                  </Typography>
                  <Box sx={{ height:1.5, width:{ xs:12, md:20 }, bgcolor:'rgba(255,255,255,0.4)', borderRadius:1 }} />
                </Box>
                {/* One word, two weights. "My" carried lighter makes the
                    mark read as a name rather than as two stacked nouns —
                    and it fits on one line at every width, which the
                    three-line "School / Management / System" never did. */}
                <Typography component="h1" sx={{
                  color:'#fff', lineHeight:1.05, letterSpacing:-1,
                  fontSize:{ xs:30, sm:40, md:52 }, fontWeight:800,
                }}>
                  <Box component="span" sx={{ fontWeight:300, opacity:0.75 }}>My</Box>School
                </Typography>
                <Box sx={{ display:'flex', alignItems:'center', gap:0.75, mt:{ xs:0.4, md:1 } }}>
                  <VerifiedIcon sx={{ fontSize:14, color:'#FCD116' }} />
                  <Typography variant="caption" sx={{
                    color:'rgba(255,255,255,0.7)', fontWeight:600,
                    fontSize:{ xs:9.5, md:10.5 },
                  }}>
                    One platform · Four portals
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Everything below the brand is the laptop hero. A phone
                gets the banner above and then the form — nothing here is
                needed to sign in. */}
            <Box sx={{ display:{ xs:'none', lg:'block' } }}>
              <Box sx={{
                p:2.5, mb:3, borderRadius:2.5,
                bgcolor:'rgba(255,255,255,0.08)',
                border:'1px solid rgba(255,255,255,0.12)',
                borderLeft:'3px solid rgba(255,255,255,0.55)',
                backdropFilter:'blur(6px)',
                animation: rise(120),
              }}>
                <Typography variant="body2" sx={{ color:'rgba(255,255,255,0.88)', lineHeight:1.8, fontSize:13 }}>
                  One school, four ways in — students, teachers, management and parents,
                  each signing in to the part that belongs to them.
                </Typography>
              </Box>

              {/* THE FOUR PORTALS. This replaced a six-tile list of
                  features (registry, permissions, transcripts…) that
                  described the plumbing. Nobody signing in cares which
                  modules exist; they care whether their portal is one of
                  them, and four tiles answer that at a glance. */}
              <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:1.5 }}>
                {[
                  { icon:<SchoolIcon sx={{ fontSize:20 }}/>, label:'Students', sub:'Results, attendance & fees' },
                  { icon:<BadgeIcon  sx={{ fontSize:20 }}/>, label:'Teachers', sub:'Register, scores & remarks' },
                  { icon:<AdminIcon  sx={{ fontSize:20 }}/>, label:'Management', sub:'Admissions, finance & staff' },
                  { icon:<ParentIcon sx={{ fontSize:20 }}/>, label:'Parents',  sub:"Your child's progress" },
                ].map((f, i) => (
                  <Box key={f.label} sx={{
                    p:1.9, borderRadius:2.5,
                    bgcolor:'rgba(255,255,255,0.06)',
                    border:'1px solid rgba(255,255,255,0.09)',
                    display:'flex', alignItems:'center', gap:1.4,
                    animation: rise(200 + i * 90),
                    transition:'transform 0.25s cubic-bezier(0.22,1,0.36,1), background-color 0.25s, border-color 0.25s',
                    '@media (hover: hover)': {
                      '&:hover':{
                        bgcolor:'rgba(255,255,255,0.13)',
                        borderColor:'rgba(255,255,255,0.24)',
                        transform:'translateY(-3px)',
                      },
                    },
                  }}>
                    <Box sx={{
                      width:36, height:36, borderRadius:2, flexShrink:0,
                      bgcolor:'rgba(255,255,255,0.14)',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      color:'#fff',
                    }}>{f.icon}</Box>
                    <Box sx={{ minWidth:0 }}>
                      <Typography sx={{ color:'#fff', fontWeight:700, fontSize:13, lineHeight:1.25 }}>
                        {f.label}
                      </Typography>
                      <Typography noWrap sx={{ color:'rgba(255,255,255,0.55)', fontSize:10.5 }}>
                        {f.sub}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mt:2.5, animation: rise(580) }}>
                {['Verified Accounts', 'Secure Access', 'Real-time Data'].map((label) => (
                  <Box key={label} sx={{
                    px:1.4, py:0.55, borderRadius:20,
                    bgcolor:'rgba(255,255,255,0.09)',
                    border:'1px solid rgba(255,255,255,0.15)',
                  }}>
                    <Typography sx={{ color:'rgba(255,255,255,0.82)', fontWeight:600, fontSize:10.5 }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Typography variant="caption" sx={{
            display:{ xs:'none', lg:'block' },
            position:'absolute', bottom:16, color:'rgba(255,255,255,0.3)',
            textAlign:'center', zIndex:1, letterSpacing:0.3,
          }}>
            © {year} MySchool · All rights reserved
          </Typography>
        </Box>

        {/* RIGHT PANEL — Form */}
        <Box sx={{
          flex: { xs: '1 1 auto', lg: '1 1 45%' },
          display:'flex', alignItems:'center', justifyContent:'center',
          p: { xs:2.5, sm:4, md:5, lg:6 },
          bgcolor:'#f0f2f8',
          backgroundImage:`url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='rgba(0,0,0,0.03)' stroke-width='1'%3E%3Crect x='0' y='0' width='60' height='60'/%3E%3C/g%3E%3C/svg%3E")`,
        }}>
          <Box sx={{ width:'100%', maxWidth:440 }}>

            {/* The mobile brand block that used to sit here is gone: the
                banner directly above it now carries the crest and the
                name, and repeating both a thumb-width apart wasted the
                one screenful a phone has. */}
            <Card sx={{
              borderRadius:4,
              boxShadow:'0 20px 60px rgba(0,0,0,0.13), 0 4px 16px rgba(0,0,0,0.06)',
              border:'1px solid rgba(0,0,0,0.06)',
              overflow:'hidden',
              animation: rise(160),
            }}>
              <Box sx={{
                height:6, position:'relative', overflow:'hidden',
                background:`linear-gradient(90deg, ${p2} 0%, ${p} 60%, #7c3aed 100%)`,
                '&::after': {
                  content:'""', position:'absolute', inset:0,
                  background:'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0) 100%)',
                  backgroundSize:'200% 100%',
                  animation:'login-shine 3.2s linear infinite',
                },
              }} />

              <CardContent sx={{ p:{ xs:2.75, sm:4, md:5 } }}>

                <Box sx={{ textAlign:'center', mb:4 }}>
                  <Box sx={{
                    position:'relative',
                    width:68, height:68, borderRadius:'50%', mx:'auto', mb:2,
                    background:`linear-gradient(135deg, ${p2} 0%, ${p} 100%)`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow:`0 8px 24px ${p}55`,
                  }}>
                    {/* Pulsing ring radiating out from the lock — system heartbeat */}
                    <Box sx={{
                      position:'absolute', inset:0, borderRadius:'50%',
                      border:`2px solid ${p}`, pointerEvents:'none',
                      animation:'login-ring 2.4s ease-out infinite',
                    }} />
                    <LockOutlined sx={{ color:'#fff', fontSize:30, position:'relative' }} />
                  </Box>
                  <Typography variant="h4" fontWeight={800} color="text.primary" lineHeight={1.15} letterSpacing={-0.5}>
                    Welcome Back
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.75}>
                    Sign in with your school portal credentials
                  </Typography>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mt:2.5 }}>
                    <Box sx={{ flex:1, height:'1px', bgcolor:'divider' }} />
                    <Typography variant="caption" color="text.disabled" fontWeight={600} sx={{ textTransform:'uppercase', letterSpacing:1, fontSize:9.5 }}>
                      Authorised Personnel Only
                    </Typography>
                    <Box sx={{ flex:1, height:'1px', bgcolor:'divider' }} />
                  </Box>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb:3, borderRadius:2.5, fontSize:13 }} onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}

                <Box sx={{ mb:2.5 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    sx={{ mb:0.8, display:'block', textTransform:'uppercase', letterSpacing:1, fontSize:10 }}>
                    Username or Email
                  </Typography>
                  <TextField fullWidth name="username" placeholder="Enter your username or email"
                    value={form.username} onChange={handleChange}
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius:2.5, bgcolor:'#fff',
                        // 16px on a phone, and not a pixel less: iOS
                        // Safari zooms the whole page in when a field
                        // smaller than that takes focus, and the user is
                        // left pinching back out to find the button.
                        fontSize:{ xs:16, sm:14 },
                        transition:'box-shadow 0.2s',
                        '&:hover fieldset': { borderColor: p },
                        '&.Mui-focused': { boxShadow:`0 0 0 4px ${p}18` },
                        '&.Mui-focused fieldset': { borderColor: p, borderWidth:2 },
                      },
                    }}
                    inputProps={{ style:{ padding:'14px 16px' } }}
                  />
                </Box>

                <Box sx={{ mb:3.5 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    sx={{ mb:0.8, display:'block', textTransform:'uppercase', letterSpacing:1, fontSize:10 }}>
                    Password
                  </Typography>
                  <TextField fullWidth name="password" placeholder="Enter your password"
                    type={showPass ? 'text' : 'password'}
                    value={form.password} onChange={handleChange}
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius:2.5, bgcolor:'#fff',
                        // 16px on a phone, and not a pixel less: iOS
                        // Safari zooms the whole page in when a field
                        // smaller than that takes focus, and the user is
                        // left pinching back out to find the button.
                        fontSize:{ xs:16, sm:14 },
                        transition:'box-shadow 0.2s',
                        '&:hover fieldset': { borderColor: p },
                        '&.Mui-focused': { boxShadow:`0 0 0 4px ${p}18` },
                        '&.Mui-focused fieldset': { borderColor: p, borderWidth:2 },
                      },
                    }}
                    inputProps={{ style:{ padding:'14px 16px' } }}
                    InputProps={{ endAdornment:(
                      <InputAdornment position="end">
                        <Tooltip title={showPass ? 'Hide password' : 'Show password'}>
                          <IconButton onClick={() => setShowPass(!showPass)} edge="end" size="small" sx={{ mr:0.5 }}>
                            {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )}}
                  />
                </Box>

                <Button fullWidth variant="contained" size="large"
                  onClick={handleSubmit} disabled={loading}
                  sx={{
                    py:1.75, fontSize:15.5, fontWeight:800, borderRadius:2.5,
                    background: loading ? undefined : `linear-gradient(90deg, ${p2} 0%, ${p} 60%, #7c3aed 100%)`,
                    boxShadow:`0 6px 24px ${p}50`,
                    letterSpacing:0.3,
                    // The lift is for a mouse only. On a touch screen
                    // :hover latches after the tap, leaving the button
                    // stuck in its raised state until something else is
                    // touched.
                    '@media (hover: hover)': {
                      '&:hover': {
                        background:`linear-gradient(90deg, ${p} 0%, ${p2} 100%)`,
                        boxShadow:`0 8px 28px ${p}66`,
                        transform:'translateY(-1px)',
                      },
                    },
                    '&:active': { transform:'translateY(1px)' },
                    transition:'all 0.25s',
                  }}>
                  {loading
                    ? <><CircularProgress size={19} color="inherit" sx={{ mr:1.5 }} /> Signing in…</>
                    : 'Sign In to Your Account'
                  }
                </Button>

                <Box sx={{
                  mt:3, p:2, borderRadius:2.5,
                  bgcolor:'#fff',
                  border:`1px solid ${p}22`,
                  display:'flex', gap:1.5, alignItems:'flex-start',
                }}>
                  <Box sx={{
                    width:32, height:32, borderRadius:1.5, flexShrink:0,
                    bgcolor:`${p}12`, display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    <AdminIcon sx={{ fontSize:17, color:p }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" fontWeight={800} color="text.primary" display="block" mb={0.3}>
                      Restricted Academic System
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight:1.6, fontSize:11.5 }}>
                      Unauthorised access is prohibited and may be subject to disciplinary action.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Box sx={{ textAlign:'center', mt:3 }}>
              <Typography variant="caption" color="text.disabled" sx={{ lineHeight:2 }}>
                Need access? Contact your system administrator.
              </Typography>
              <br />
              <Typography variant="caption" color="text.disabled">
                © {year} MySchool
              </Typography>
            </Box>
          </Box>
        </Box>

      </Box>
    </>
  );
}
